import {
  LitElement,
  html,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * "Ejects" from the shadow DOM.
 * Advantage: The element and it's children will be styled according to (cascading) global style selectors.
 * Disadvantage: slots won't work within these elements.
 */
class LitElementEjected extends LitElement {
  createRenderRoot() {
    // Returning `this` will render the template directly into the light DOM
    return this;
  }
}

export class SubscriptionWidget extends LitElementEjected {
  static properties = {
    productId: { type: String },
    variantId: { type: String },
    formId: { type: String },
    _product: { type: Object, state: true },
    _variants: { type: Array, state: true },
    _selectedVariant: { type: Object, state: true },
    _selectedOption: { type: String, state: true },
    _selectedPlan: { type: Object, state: true },
    _plans: { type: Array, state: true },
    _loadingState: { type: String, state: true },
    _quantity: { type: Number, state: true },
    _slashedPrice: { type: String, state: true },
    _actualPrice: { type: String, state: true },
  };

  constructor() {
    super();
    this.productId = null;
    this.variantId = null;
    this.formId = null;
    this._product = null;
    this._variants = [];
    this._selectedVariant = null;
    this._selectedPlan = null;
    this._plans = [];
    this._loadingState = "idle";
    this._selectedOption = null;
    this._slashedPrice = undefined;
    this._actualPrice = undefined;
    this._quantity = 1;
  }

  connectedCallback() {
    super.connectedCallback();
    this._init();
  }

  updated(changedProps) {
    //update pricing when variant id changes
    if (changedProps.has("variantId")) {
      if (
        changedProps.get("variantId") !== undefined &&
        this._loadingState === "idle"
      ) {
        this._setupProductState();
      }
    }
  }

  async _init() {
    if (!this.productId || !this.variantId) {
      console.error("Product ID or Variant ID is missing");
      return;
    }

    this._loadingState = "loading";

    try {
      await this._fetchProductData();
      this._loadingState = "idle";
    } catch (error) {
      console.error("Error initializing widget:", error);
      this._loadingState = "error";
    }
  }

  async _fetchProductData() {
    const session = await recharge.auth.loginShopifyAppProxy();
    const settings = await recharge.cdn.getCDNProductAndSettings(
      this.productId,
    );

    const product_settings = settings.product;
    const productQuery = await recharge.product.productSearch(session, {
      format_version: "2022-06",
      external_product_ids: [this.productId],
    });

    if (!productQuery.products || productQuery.products.length === 0) {
      throw new Error("Product not found");
    }

    this._product = productQuery.products[0];

    console.log('settings', settings)
    console.log('product_settings', this._product)
    this._variants = product_settings.variants.filter(
      (v) => v.is_published !== false,
    );

    this._setupProductState();
  }

  _setupProductState() {
    this._selectedVariant = this._variants.find(
      (v) => Number(v.id) === Number(this.variantId),
    );

    if (!this._selectedVariant) {
      throw new Error("Selected variant not found");
    }

    this._plans = this._product.plans
      .filter((plan) => plan.type !== "onetime")
      .sort((a, b) => a.sort_order - b.sort_order);

    this._selectedPlan = this._plans.length > 0 ? this._plans[0] : null;
    this._selectedOption = "subscriptions";

    if (this._selectedPlan) {
      this._updatePricing();
    }
  }

  _updatePricing() {
    if (!this._selectedVariant || !this._selectedPlan) return;

    for (const plan of this._plans) {
      if (!this._selectedVariant.selling_plan_allocations) continue;

      const allocation = this._selectedVariant.selling_plan_allocations.find(
        (price) =>
          price.selling_plan_id.toString() === plan.external_plan_id.toString(),
      );

      if (allocation) {
        plan.actualPrice = `$${allocation.discounted_price}`;
        plan.slashedPrice = `$${this._selectedVariant.prices.unit_price}`;
      } else {
        plan.actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      }
    }


    if (this._selectedOption === "subscriptions" && this._selectedPlan) {
      this._actualPrice = this._selectedPlan.actualPrice;
      this._slashedPrice = this._selectedPlan.slashedPrice;

      console.log("Updated pricing:", {
        actualPrice: this._actualPrice,
        slashedPrice: this._slashedPrice,
        selectedOption: this._selectedOption,
        selectedPlan: this._selectedPlan,
      });
    }
  }

  _handleRadioChange(event) {
    this._selectedOption = event.target.value;
    this._updatePricing();
    this.requestUpdate();
  }

  _updateFrequency(event) {
    const externalPlanId = event.target.value;
    this._selectedPlan = this._plans.find((p) => p.external_plan_id.toString() === externalPlanId.toString());
    this.requestUpdate();
    this._updatePricing();
  }

  _handleSellingPlanChange(event) {
    const selectedPlanId = event.target.value;
    this._selectedPlan = this._plans.find(
      (plan) => plan.external_plan_id === Number(selectedPlanId),
    );
    this._updatePricing();
    this.requestUpdate();
  }

  _renderFrequencyOption(plan) {
    if (
      plan.external_variant_ids.length > 0 &&
      !plan.external_variant_ids.find(
        (id) => Number(id.ecommerce) === Number(this.variant_id),
      )
    ) return;

    const planText = html`Every ${plan.subscription_preferences.order_interval_frequency} ${plan.subscription_preferences.interval_unit}${plan.subscription_preferences.charge_interval_frequency > 1 ? "s" : ""}`;
    // const planText = plan.title;

    return html`
      <option
        .value=${plan.external_plan_id}
        data-interval="${plan.subscription_preferences
          .order_interval_frequency}"
        ${this._selectedPlan.id === plan.id ? html`selected` : ""}
      >
        ${planText}
      </option>
    `;
  }

  render() {
    if (this._loadingState === "loading") {
      return html`<div class="sms-recharge-widget__loading">Loading...</div>`;
    }

    if (
      this._loadingState === "error" ||
      !this._product ||
      !this._selectedVariant
    ) {
      return html`<div class="sms-recharge-widget__error">
        Unable to load subscription options
      </div>`;
    }

    const showSubscriptionOption = this._plans && this._plans.length > 0;

    if (!showSubscriptionOption) return html``;

    return html`
      <div class="sms-recharge-widget">
        <label class="sms-widget__select-label" for="sms-frequency-${this.formId}">Frequency</label>
        <div class="sms-widget__select-wrapper sms-pc__form-group ${this._plans.length > 1 ? '' : 'select-single'}">
          ${this._plans.length > 1 ? html`
            <div class="sms-rw__options-container">
              <select
                class="sms-widget__select"
                name="selling_plan"
                id="sms-frequency-${this.formId}"
                @change=${this._updateFrequency}
              >
                ${this._plans.map((plan) => this._renderFrequencyOption(plan))}
              </select>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </div>
          ` : html`
            <span class="sms-widget__select-single">Every ${this._plans[0].subscription_preferences.order_interval_frequency} ${this._plans[0].subscription_preferences.interval_unit}${this._plans[0].subscription_preferences.charge_interval_frequency > 1 ? "s" : ""}</span>
            <input type="hidden" name="selling_plan" .value=${this._plans[0].external_plan_id} />
          `}
        </div>
      </div>
    `;
  }
}

customElements.define("sms-recharge-widget", SubscriptionWidget);
